import { differenceInCalendarDays, format } from "date-fns";
import { useFormik } from "formik";
import * as Yup from "yup";
import { requiredIfPredicate } from "../../../util/validation";
import { AutoCompleteItem } from "../../autocomplete/interface";
import {
  FirmaRelationKode,
  FirmaRelationKodeDict,
} from "../OpretPerson/useOpretPerson";
import { useMutation } from "@tanstack/react-query";
import { post } from "../../../api/util";
import { BeskikAdvokatDTO } from "../BeskikketAdvokat/useBeskikketAdvokat";
import { PersonDto } from "../../../interfaces/schema";
import { dateIsWithinInterval } from "../../../util/date";

export function useGenudleverAdvokat(person: PersonDto) {
  const { personFirma } = person;
  const genudleverValidation = useFormik<GenudleverAdvokatFormik>({
    validateOnMount: true,
    initialValues: {
      firmaRelation: FirmaRelationKodeDict.ansat,
      erUdenlandskAdvokat: false,
      udenlandskAdvokatSamfund: undefined,
      beskikkelseFraDato: undefined,
      aarsag: "",
      emailArbejde: "",
      nytFirma: undefined,
      nytFirmaFraDato: undefined,
      sammeFirma: 1,
    },
    validationSchema: Yup.object({
      udenlandskAdvokatSamfund: Yup.object().test(
        "udenlandskAdvokatSamfund",
        "Udenlandsk advokatsamfund skal være sat når personen er EU-advokat",
        function (udenlandskAdvokatSamfund) {
          const erUdenlandskAdvokat = this.parent.erUdenlandskAdvokat;
          if (erUdenlandskAdvokat) {
            return udenlandskAdvokatSamfund !== undefined;
          }
          return true;
        },
      ),
      beskikkelseFraDato: Yup.date()
        .nullable()
        .required("Startdato skal være sat "),
      aarsag: Yup.string(),
      nytFirma: Yup.object()
        .test(
          "sammefirma",
          "Nyt firma skal være sat, dersom personen ikke fortsætter i samme firma",
          function (nytFirma) {
            return requiredIfPredicate(this.parent.sammeFirma > 1, nytFirma);
          },
        )
        .test(
          "nytFirma",
          "Nyt firma skal være sat, dersom personen ikke har aktiv firmarelation",
          function (nytFirma) {
            const compareDate = this.parent.beskikkelseFraDato
              ? this.parent.beskikkelseFraDato
              : new Date();
            const aktiveFirma = person.personFirma?.filter(
              (element) =>
                differenceInCalendarDays(
                  new Date(element?.periode.tilDatoInklusiv),
                  compareDate,
                ) >= 0,
            );
            return requiredIfPredicate(aktiveFirma?.length === 0, nytFirma);
          },
        ),
      emailArbejde: Yup.string().email("Email har forkert format"),
      nytFirmaFraDato: Yup.date()
        .nullable()
        .test(
          "nytFirmaFraDato",
          "Start dato skal være sat",
          function (emailArbejde) {
            const compareDate = this.parent.beskikkelseFraDato
              ? this.parent.beskikkelseFraDato
              : new Date();
            const aktiveFirma = person.personFirma?.filter(
              (element) =>
                differenceInCalendarDays(
                  new Date(element?.periode.tilDatoInklusiv),
                  compareDate,
                ) >= 0,
            );
            return requiredIfPredicate(
              aktiveFirma?.length === 0 || this.parent.sammeFirma > 1,
              emailArbejde,
            );
          },
        ),
    }),
    onSubmit: (values) => {
      const {
        aarsag,
        emailArbejde,
        beskikkelseFraDato,
        erUdenlandskAdvokat,
        nytFirma,
        firmaRelation,
        udenlandskAdvokatSamfund,
        sammeFirma,
        nytFirmaFraDato,
      } = values;

      const aktiveFirmaer = personFirma?.filter((pf) =>
        beskikkelseFraDato
          ? dateIsWithinInterval(
              beskikkelseFraDato,
              new Date(pf.periode.fraDato),
              new Date(pf.periode.tilDatoInklusiv),
            )
          : false,
      );

      const dto: BeskikAdvokatDTO = {
        aarsag,
        udenlandskAdvokatSamfundId:
          erUdenlandskAdvokat && udenlandskAdvokatSamfund
            ? udenlandskAdvokatSamfund.id
            : undefined,
        fraDato: beskikkelseFraDato
          ? format(beskikkelseFraDato, "yyyy-MM-dd")
          : "",
        nyPersonFirmarelation:
          nytFirma && (aktiveFirmaer?.length === 0 || sammeFirma > 1)
            ? {
                firmaId: nytFirma.id,
                personfirmatypeNr: firmaRelation.kode,
                periodeFraDato: nytFirmaFraDato
                  ? format(nytFirmaFraDato, "yyyy-MM-dd")
                  : "",
                emailArbejde: emailArbejde ? emailArbejde : undefined,
              }
            : undefined,
        straksDeponering: false,
        fortsaettendePersonFirmarelationer:
          sammeFirma < 3 ? aktiveFirmaer?.map((af) => af.id) : [],
        ophoerendePersonFirmarelationer:
          sammeFirma === 3 ? aktiveFirmaer?.map((af) => af.id) : [],
      };
      genudleverAdvokatQuery.mutate(dto);
    },
  });

  const genudleverAdvokatQuery = useMutation({
    mutationFn: (dto: BeskikAdvokatDTO) => {
      return post<{ id: string; fornavne: string; efternavn: string }>({
        query: `person/${person.id}/wizards/genudleveradvokat`,
        body: dto,
      });
    },
  });

  return { formik: genudleverValidation, genudleverAdvokatQuery };
}

export type GenudleverAdvokatFormik = {
  erUdenlandskAdvokat: boolean;
  udenlandskAdvokatSamfund: IUdenlandskAdvokatsamfund | undefined;
  beskikkelseFraDato: Date | undefined;
  aarsag: string;
  emailArbejde: string;
  sammeFirma: 1 | 2 | 3; // 1 - Ja, 2 - Ja, men også nyt. 3 - Nej (skal vælge ny relation)
  nytFirma: AutoCompleteItem | undefined;
  firmaRelation: FirmaRelationKode;
  nytFirmaFraDato: Date | undefined;
};
